import React from 'react';
import { Dialog } from '../common/Dialog';
import { useGetUserByIdQuery } from '../../services/userApi';

interface UserDetailsDialogProps {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<boolean | void>;
  canEditRoles: boolean;
  /** Used to warn when an admin removes their own admin role */
  currentUserId?: number | null;
}

export default function UserDetailsDialog({ 
  user, 
  isOpen, 
  onClose, 
  onSave,
  canEditRoles,
  currentUserId = null,
}: UserDetailsDialogProps) {
  const [formData, setFormData] = React.useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    isDisabled: false,
    isLockedOut: false,
    role: '2',
    currentPassword: '',
    password: '',
    confirmPassword: ''
  });
  
  const [showPasswordFields, setShowPasswordFields] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const { data: userDetails } = useGetUserByIdQuery(user?.id ?? 0, { 
    skip: !user?.id,
    refetchOnMountOrArgChange: true
  });

  const baselineUser = user || userDetails;
  const editingOwnAccount =
    canEditRoles && currentUserId != null && user?.id === currentUserId;
  const baselineWasAdmin = baselineUser?.role === 'admin';
  const selfDemotesToViewer =
    editingOwnAccount && baselineWasAdmin && formData.role === '2';

  React.useEffect(() => {
    // Use the user data passed as prop first, fallback to fetched userDetails
    const userData = user || userDetails;
    if (userData) {
      setFormData(prevData => ({
        ...prevData,
        email: userData.email || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phoneNumber: userData.phoneNumber || '',
        isDisabled: Boolean(userData.isDisabled),
        isLockedOut: Boolean(userData.isLockedOut),
        role: userData.role === 'admin' ? '1' : '2'
      }));
    }
  }, [user, userDetails]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selfDemotesToViewer) {
        const ok = window.confirm(
          'You are about to remove your own administrator role. After saving, you will not be able to manage users, change roles, or access other admin-only features unless another administrator restores your access.\n\nContinue?'
        );
        if (!ok) return;
      }

      // Map role value to the expected format
      const roles = formData.role === '1' ? [1] : [2];
      
      const wasUpdated = await onSave({
        ...formData,
        roles
      });
      
      if (wasUpdated) {
        setSuccessMessage('User updated successfully');
      } else {
        setSuccessMessage('No changes made');
      }
      setShowPasswordFields(false);
    } catch (error) {
      setSuccessMessage('Failed to update user');
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="bg-app-panel dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
          {user ? 'Edit User' : 'Add User'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="+44"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isDisabled"
                checked={formData.isDisabled}
                onChange={(e) => setFormData({ ...formData, isDisabled: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="isDisabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Disable User
              </label>
            </div>
          </div>

          {canEditRoles && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              >
                <option value="2">Viewer</option>
                <option value="1">Admin</option>
              </select>
              {selfDemotesToViewer && (
                <div
                  role="alert"
                  className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                >
                  Changing your role to Viewer will remove your ability to manage users and other
                  administrator-only actions. Another admin must reinstate your access if you need
                  it back.
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setShowPasswordFields(!showPasswordFields)}
              className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Change Password
            </button>
          </div>

          {showPasswordFields && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Password
                </label>
                <input
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  New Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>
            </div>
          )}

          {successMessage && (
            <div className="text-sm text-green-600 dark:text-green-400">
              {successMessage}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}